// File: src/pages/reveal/RevealIcebreakers.jsx
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import RevealPortalNavBar from "../../components/Reveal/RevealPortalNavBar";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";

const PRESET_ICEBREAKERS = [
  "Two truths and a lie—I'll start. Guess which one is the lie?",
  "What’s the most spontaneous thing you’ve done in the last year?",
  "Pick one: sunrise walk or midnight dessert crawl—and why?",
  "If we had 30 minutes right now, what mini adventure would you choose?",
  "What’s a tiny hill you’ll absolutely die on? (e.g., pineapple on pizza)",
  "Show & Tell: what’s the last photo in your camera roll you love?",
  "Perfect first collab: coffee + bookstore, or tacos + people-watching?",
  "What city feels like your personality—and what’s the vibe?",
  "Hot take time: name a movie you think is overrated.",
  "What skill do you wish you could download instantly?",
];

export default function RevealIcebreakers() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();

  const [idx, setIdx] = useState(0);
  const active = useMemo(() => PRESET_ICEBREAKERS[idx], [idx]);

  const total = PRESET_ICEBREAKERS.length;
  const canPrev = total > 1;
  const canNext = total > 1;

  const prev = () => setIdx((i) => (i === 0 ? total - 1 : i - 1));
  const next = () => setIdx((i) => (i === total - 1 ? 0 : i + 1));

  return (
    <div style={S.page}>
      <RevealPortalNavBar eventId={eventId} title="Icebreakers" />

      <main style={S.main}>
        {/* Header block (crooked blue highlight like CodeVerificationPage) */}
        <div style={S.headerWrap}>
          <h2 style={S.headerTitle}>
            Start{""}
            <span style={S.headerHighlight}>
              <span style={S.headerHighlightText}>Chatting</span>
            </span>
          </h2>
          <div style={S.headerSubheading}>
            Use these icebreakers to chat with your match.
          </div>
        </div>

        {/* Stacked card — mirrors FoundMatch stack */}
        <div style={S.bleedWrap}>
          <div style={S.stackWrap}>
            <div style={S.backCard} aria-hidden />
            <div style={S.frontCard}>
              <span style={S.pill}>Icebreaker</span>
              <h2 style={S.question}>{active}</h2>
            </div>
          </div>
        </div>

        {/* Pagination row */}
        <div style={S.pagerRow} role="group" aria-label="Icebreaker pagination">
          <button
            type="button"
            onClick={prev}
            disabled={!canPrev}
            aria-disabled={!canPrev}
            style={{ ...S.pagerBtn, ...(!canPrev ? S.pagerBtnDisabled : {}) }}
          >
            <LuChevronLeft style={S.pagerIcon} aria-hidden />
          </button>
          <span style={S.pagerText}>{idx + 1}/{total}</span>
          <button
            type="button"
            onClick={next}
            disabled={!canNext}
            aria-disabled={!canNext}
            style={{ ...S.pagerBtn, ...(!canNext ? S.pagerBtnDisabled : {}) }}
          >
            <LuChevronRight style={S.pagerIcon} aria-hidden />
          </button>
        </div>
      </main>

      {/* Fixed bottom back button */}
      <div style={S.actionBar}>
        <button onClick={() => navigate(-1)} style={S.actionSecondary}>
          Back to Chat
        </button>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    background: "#fff",
    display: "flex",
    flexDirection: "column",
  },
  main: {
    flex: 1,
    maxWidth: 760,
    width: "92%",
    margin: "12px auto 24px",
  },

  /* Header block (crooked blue highlight) */
  headerWrap: {
    boxSizing: "border-box",
    maxWidth: 680,
    width: "80%",
    margin: "12px auto 20px",
    padding: "0 12px",
    textAlign: "center",
    color: "#0b0b0b",
  },
  headerTitle: {
    margin: 0,
    fontSize: "1.85rem",
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: "-0.01em",
  },
  headerHighlight: {
    display: "inline-block",
    backgroundColor: "#3579F6",
    color: "#fff",
    padding: "3px 8px 4px",
    borderRadius: 6,
    transform: "skewX(-6deg) rotate(-2deg)",
    marginLeft: 6,
  },
  headerHighlightText: {
    display: "inline-block",
    transform: "skewX(6deg) rotate(2deg)",
  },
  headerSubheading: {
    marginTop: 6,
    fontSize: ".9rem",
    color: "#6b7280",
    fontWeight: 500,
    lineHeight: 1.45,
  },

  /* Stack from FoundMatch */
  bleedWrap: {
    position: "relative",
    width: "100vw",
    left: "50%",
    right: "50%",
    marginLeft: "-50vw",
    marginRight: "-50vw",
  },
  stackWrap: {
    position: "relative",
    width: "min(86vw, 600px)",
    margin: "12px auto 16px",
    height: 465,
  },
  backCard: {
    position: "absolute",
    left: 8,
    right: 8,
    bottom: -10,
    height: 300,
    borderRadius: 18,
    background: "#fafafa",
    transform: "rotate(4.2deg) translateY(6px)",
    transformOrigin: "center",
    boxShadow: "0 14px 28px rgba(15,23,42,0.14)",
  },
  frontCard: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    height: 300,
    borderRadius: 18,
    border: "1px solid #eee",
    background: "#fff",
    overflow: "hidden",
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    textAlign: "center",
    padding: "7.5rem 1.5rem 2.5rem",
    boxShadow: "0 14px 28px rgba(105,105,105,0.14)",
  },

  pill: {
    position: "absolute",
    top: 80,
    left: "50%",
    transform: "translateX(-50%)",
    color: "#555",
    fontWeight: 800,
    padding: "8px 16px",
    fontSize: ".8rem",
    background: "#eee",
    borderRadius: 100,
    border: "1px solid #ddd",
  },
  question: {
    fontSize: "1.75rem",
    fontWeight: 550,
    lineHeight: 1.5,
    letterSpacing: "-0.02em",
    textRendering: "optimizeLegibility",
    margin: "0 auto",
    maxWidth: 360,
    color: "#111",
    marginTop: "0.75rem",
  },

  /* Pager row */
  pagerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    margin: "60px 0 2px",
  },
  pagerBtn: {
    height: 40,
    width: 40,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#0f172a",
    cursor: "pointer",
  },
  pagerBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
  pagerIcon: { fontSize: 16 },
  pagerText: {
    minWidth: 48,
    textAlign: "center",
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: ".5em",
    color: "#0f172a",
  },

  /* Fixed bottom action bar */
  actionBar: {
    position: "fixed",
    inset: "auto 0 0 0",
    background: "#fff",
    borderTop: "1px solid #eef0f2",
    display: "flex",
    padding: "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))",
    maxWidth: 760,
    margin: "0 auto",
    left: "50%",
    transform: "translateX(-50%)",
    width: "100%",
    zIndex: 50,
  },
  actionSecondary: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#0b0b0b",
    fontSize: 12,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
};
